import {Component, computed, inject, Input} from '@angular/core';
import {AbstractControl, FormBuilder, ValidationErrors, Validators} from '@angular/forms';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {Trip} from '../../model/trip';
import {AdxPaymentService} from '../../../payments/service/adx-payment.service';
import {AdxPaymentModel} from '../../../payments/models/adx-payment';
import {AzureAuthService} from 'src/infrastructure/services/azure-auth.service';
import {AdxInvoiceErpAdjustmentGridService} from '../../../adx-invoice-erp-adjustment/service/adx-invoice-erp-adjustment-grid.service';

@Component({
    selector: 'app-trip-info',
    templateUrl: './trip-info.component.html',
    standalone: false
})

@AutoUnsubscribe()
export class TripInfoComponent {
  @Input() public data: any;
  _trip!: Trip;
  paymentTotals = {
    totalOpenPaymentsReceivedFromClient: 0,
    totalClosedPaymentsReceivedFromClient: 0,
    totalOpenPaymentsMadeToVendor: 0,
    totalClosedPaymentsMadeToVendor: 0
  };
  isEditable: boolean = false;
  currentDate: string;
  tripPayments: AdxPaymentModel[] = [];
  tripAdjustmentTotal = 0;

  private readonly azureAuthService = inject(AzureAuthService);
  private readonly adxInvoiceErpAdjustmentGridService = inject(AdxInvoiceErpAdjustmentGridService);
  protected readonly loggedInUserHasDirectorOrFinanceOrManagerRole = computed(() => (this.azureAuthService.checkUserAzureTokenHasManagerRole() && this.azureAuthService.checkUserAzureTokenHasFinanceRole() && this.azureAuthService.checkUserAzureTokenHasDirectorRole()));

  constructor(
    private buildr: FormBuilder,
    private adxPaymentService: AdxPaymentService) {
      this.currentDate = new Date().toISOString().split('T')[0];
  }

  ngOnInit() {
    this.getTripInformation();
    this.tripInfoForm.get('tripStartDate')?.valueChanges.subscribe(() => {
      this.tripInfoForm.get('tripEndDate')?.updateValueAndValidity();
    });
  }

  getTripInformation() {
    this._trip = this.data.trip;
    
    this.patchForm(this._trip);
    this.fetchAndCalculatePaymentTotals(this._trip.id);
    this.fetchAndCalculateAdjustmentTotal(this._trip.id);
  }

  fetchAndCalculateAdjustmentTotal(tripId: number) {
    this.tripAdjustmentTotal = 0;

    this.adxInvoiceErpAdjustmentGridService.getItems({filters: {tripId: tripId.toString()}} as any)
      .subscribe({
        next: (response) => {
          this.tripAdjustmentTotal = (response.items || [])
            .reduce((total, item) => total + (item.salesPrice || 0), 0);
        },
        error: (error) => {
          console.error('Error fetching trip adjustment total:', error);
        }
      });
  }

  fetchAndCalculatePaymentTotals(tripId: number) {
    this.paymentTotals = {
      totalOpenPaymentsReceivedFromClient: 0,
      totalClosedPaymentsReceivedFromClient: 0,
      totalOpenPaymentsMadeToVendor: 0,
      totalClosedPaymentsMadeToVendor: 0
    };

    
    if (this._trip && this._trip.adxInvoices && this._trip.adxInvoices.length > 0) {      
      this._trip.adxInvoices.forEach(invoice => {
        const invoiceAny = invoice as any;
        
        if (invoiceAny.adxBookings && Array.isArray(invoiceAny.adxBookings)) {
          
          invoiceAny.adxBookings.forEach((booking: any) => {
            if (booking.isDeleted) {
              return;
            }
            
            const amount = booking.totalFare || 0;
            
            if (typeof booking.clientPayStatusName === 'string') {
              if (booking.clientPayStatusName === 'Open') {
                this.paymentTotals.totalOpenPaymentsReceivedFromClient += amount;
              } else if (booking.clientPayStatusName === 'Closed') {
                this.paymentTotals.totalClosedPaymentsReceivedFromClient += amount;
              }
            }
            
            if (typeof booking.vendorPayStatusName === 'string') {
              if (booking.vendorPayStatusName === 'Open') {
                this.paymentTotals.totalOpenPaymentsMadeToVendor += amount;
              } else if (booking.vendorPayStatusName === 'Closed') {
                this.paymentTotals.totalClosedPaymentsMadeToVendor += amount;
              }
            }
          });
        }
      });
      
    }
    if (this.loggedInUserHasDirectorOrFinanceOrManagerRole()) {
      this.adxPaymentService.searchAdxPayment({
        filters: { tripId: tripId.toString() },
        page: { pageSize: 1000 }
      }).subscribe({
        next: (response) => {
          if (response.items && response.items.length > 0) {
            this.tripPayments = response.items;
          }
        },
        error: (error) => {
          console.error('Error fetching trip payments:', error);
        }
      });
    }
  }

  tripInfoForm = this.buildr.group({
    tripName: [{ value: '', disabled: !this.isEditable },  [Validators.required, this.tripNameLengthValidator]],
    tripTotal: [{ value: 0, disabled: true }],
    tripStartDate: [{ value: '', disabled: !this.isEditable }, Validators.required],
    tripEndDate: [{ value: '', disabled: !this.isEditable }, [Validators.required, this.endDateValidator.bind(this)]],
    templateNotes: this.buildr.control('')
  })

  optionalLengthValidator(maxLength: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      return value && value.length > maxLength ? { lengthTooLong: { maxLength } } : null;
    };
  }

  tripNameLengthValidator(control: AbstractControl): ValidationErrors | null {
    const tripName = control.value;
    return tripName && tripName.length > 500 ? { tripNameTooLong: true } : null;
  }

  endDateValidator(control: AbstractControl): ValidationErrors | null {
    const startDate = this.tripInfoForm?.get('tripStartDate')?.value;
    if (startDate && control.value) {
      return new Date(control.value) < new Date(startDate) ? { endDateInvalid: true } : null;
    }
    return null;
  }

  resetForm(): void {
    this.tripInfoForm.reset(this._trip);
  }

  patchForm(data: Trip){
    this.tripInfoForm?.patchValue({
      tripName: data.tripName,
      tripTotal: data.tripTotal,
      tripStartDate: data.tripStartDate,
      tripEndDate: data.tripEndDate,
    });
  }

  formatPaymentValue(value: number): string {
    const formattedValue = Math.abs(value).toFixed(2);
    return value < 0 ? `-$${formattedValue}` : `$${formattedValue}`;
  }
}

